import { Box, Paper, Typography, Stack, Chip } from "@mui/material";
import {
    Hotel,
    DirectionsCar,
    LocalActivity,
    AccessTime,
    LocationOn,
    Schedule,
    Flight,
    Star as StarIcon,
    MeetingRoom,
    FlightLand,
    FlightTakeoff,
    TransferWithinAStation,
    Login,
    Logout,
    Weekend,
    Room,
    AutoAwesome,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../utils/programs/dateCalculations";
import { PRODUCT_TYPE_CODES, PRICING_CODES } from "../ProgramForm/helpers/formConstants";

const ProgramProductItem = ({ product, index }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    // const theme = useTheme();

    const typeCode = product.product_type || product.productType?.code || '';
    const priceCode = product.pricing_code || product.pricing_unit || '';

    const getIcon = () => {
        if (typeCode === PRODUCT_TYPE_CODES.ARRIVAL_DESTINATION) return <FlightLand fontSize="medium" sx={{ fontSize: 20 }} />;
        if (typeCode === PRODUCT_TYPE_CODES.DEPARTURE_DESTINATION) return <FlightTakeoff fontSize="medium" sx={{ fontSize: 20 }} />;
        if (typeCode === PRODUCT_TYPE_CODES.TRANSFER_BETWEEN_HOTELS) return <TransferWithinAStation fontSize="medium" sx={{ fontSize: 20 }} />;
        if (typeCode === PRODUCT_TYPE_CODES.HOTEL_CHECK_IN) return <Login fontSize="medium" sx={{ fontSize: 20 }} />;
        if (typeCode === PRODUCT_TYPE_CODES.HOTEL_CHECK_OUT) return <Logout fontSize="medium" sx={{ fontSize: 20 }} />;
        if (typeCode === PRODUCT_TYPE_CODES.FREE_DAY || typeCode === PRODUCT_TYPE_CODES.FREE_TIME || priceCode === PRICING_CODES.FREE_DAY || priceCode === PRICING_CODES.FREE_TIME) return <Weekend fontSize="medium" sx={{ fontSize: 20 }} />;
        if (priceCode === PRICING_CODES.PROGRAM_MARKER) return <Room fontSize="medium" sx={{ fontSize: 20 }} />;

        if (product.category_id === 3 || typeCode === 'hotel') return <Hotel fontSize="medium" sx={{ fontSize: 20 }} />;
        if (product.category_id === 2 || typeCode === 'transfer') return <DirectionsCar fontSize="medium" sx={{ fontSize: 20 }} />;
        if (typeCode === 'flight') return <Flight fontSize="medium" sx={{ fontSize: 20 }} />;
        return <LocalActivity fontSize="medium" sx={{ fontSize: 20 }} />;
    };

    const getColor = () => {
        if (typeCode === PRODUCT_TYPE_CODES.FREE_DAY || typeCode === PRODUCT_TYPE_CODES.FREE_TIME || priceCode === PRICING_CODES.FREE_DAY || priceCode === PRICING_CODES.FREE_TIME) return "success";
        if (priceCode === PRICING_CODES.PROGRAM_MARKER) return "error";
        if (typeCode === PRODUCT_TYPE_CODES.ARRIVAL_DESTINATION || typeCode === PRODUCT_TYPE_CODES.DEPARTURE_DESTINATION || typeCode === 'flight') return "secondary";
        if (product.category_id === 3 || typeCode === 'hotel' || typeCode === PRODUCT_TYPE_CODES.HOTEL_CHECK_IN || typeCode === PRODUCT_TYPE_CODES.HOTEL_CHECK_OUT) return "info";
        if (product.category_id === 2 || typeCode === 'transfer' || typeCode === PRODUCT_TYPE_CODES.TRANSFER_BETWEEN_HOTELS) return "warning";
        return "primary";
    };
    const renderFlightRoute = () => {
        const depCity =
            product.flight_details?.departure_airport?.city?.name?.[lang];

        const arrCity =
            product.flight_details?.arrival_airport?.city?.name?.[lang];

        if (!depCity || !arrCity) return null;

        return `${depCity} → ${arrCity}`;
    };


    const isHotel = product.category_id === 3 || product.product_type === 'hotel';
    const isFlight = product.product_type === 'flight';

    return (
        <Paper
            variant="outlined"
            sx={{
                px: 2,
                py: isHotel ? 1.5 : 2,
                display: "flex",
                gap: 2,
                "@media print": {
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                },
                "&:hover": {
                    bgcolor: "action.hover",
                },
                alignItems: "center",
            }}
        >
            {/* Index */}
            <Box
                sx={{
                    minWidth: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: `${getColor()}.main`,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "1rem",
                }}
            >
                {index}
            </Box>

            {/* Details */}
            <Box sx={{ flex: 1 }}>
                {isHotel ? (
                    /* === Hotel: simplified card === */
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1 }}>
                            <Hotel sx={{ fontSize: 24, color: 'info.main' }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '15px', lineHeight: 1.3 }}>
                                {product.hotel_details?.hotel_name || ''}
                            </Typography>
                            {product.hotel_details?.rating && (
                                <Chip
                                    icon={<StarIcon sx={{ fontSize: 16 }} />}
                                    label={`${product.hotel_details.rating.stars || product.hotel_details.rating.name?.[lang] || ''}`}
                                    size="small"
                                    color="warning"
                                    variant="filled"
                                    sx={{ fontSize: '13px', fontWeight: 700, height: 26 }}
                                />
                            )}
                            {(() => {
                                const hd = product.hotel_details;
                                const roomStays = hd?.room_stays || [];
                                const categoryMap = new Map();
                                // Collect unique room categories from room_stays
                                roomStays.forEach(rs => {
                                    const catId = rs.room_category_id ?? 'default';
                                    const catName = rs.room_category?.name?.[lang] || rs.room_category?.name?.en;
                                    if (catName && !categoryMap.has(catId)) {
                                        categoryMap.set(catId, { name: catName, roomsCount: rs.rooms_count || 1 });
                                    }
                                });
                                // Fallback to hotel-level room_category if no room_stays
                                if (categoryMap.size === 0) {
                                    const fallback = hd?.room_category?.name?.[lang] || hd?.room_category?.name?.en;
                                    if (fallback) categoryMap.set('fallback', { name: fallback, roomsCount: product.rooms_count || hd?.rooms_count || 1 });
                                }
                                return Array.from(categoryMap.entries()).map(([id, data]) => {
                                    const roomWord = data.roomsCount === 1 ? (lang === 'ar' ? 'غرفة' : 'Room') : (lang === 'ar' ? 'غرف' : 'Rooms');
                                    const labelText = `${data.name} (${data.roomsCount} ${roomWord})`;

                                    return (
                                        <Chip
                                            key={id}
                                            icon={<MeetingRoom sx={{ fontSize: 16 }} />}
                                            label={labelText}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            sx={{ fontSize: '13px', fontWeight: 600, height: 26 }}
                                        />
                                    );
                                });
                            })()}
                            <Box sx={{ flex: 1 }} />
                            <Chip
                                label={lang === 'ar' ? 'إقامة' : 'Accommodation'}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: '11px', fontWeight: 600, height: 24 }}
                            />
                        </Box>
                        {product.hotel_details?.extras && product.hotel_details.extras.length > 0 && (
                            <Box sx={{ mt: 1, pl: lang === 'ar' ? 0 : { xs: 0, sm: 4.5 }, pr: lang === 'ar' ? { xs: 0, sm: 4.5 } : 0, width: '100%' }}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                    {product.hotel_details.extras.map((extra, idx) => {
                                        const extraName = extra.hotel_extra?.name?.[lang] || extra.hotel_extra?.name?.en || extra.extra_details?.name?.[lang] || extra.extra_name?.[lang] || extra.name?.[lang] || extra.name || '';
                                        const label = extra.note?.[lang]
                                            ? `${extraName} (x${extra.quantity || 1}) - ${extra.note[lang]}`
                                            : `${extraName} (x${extra.quantity || 1})`;
                                        return (
                                            <Chip
                                                key={idx}
                                                icon={<AutoAwesome sx={{ fontSize: 14 }} />}
                                                label={label}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(245,158,11,0.08)',
                                                    color: '#d97706',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    border: '1px solid',
                                                    borderColor: 'rgba(245,158,11,0.2)',
                                                    '& .MuiChip-icon': {
                                                        color: '#d97706',
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    /* === Non-hotel: full card === */
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Stack direction="row" justifyContent={'space-between'} spacing={1} flexWrap="wrap" useFlexGap>
                            <Stack direction="row" spacing={1} sx={{ flex: 1 }} alignItems="center" mb={0.5}>
                                <Box sx={{ mt: '2px', display: 'flex' }}>
                                    {getIcon()}
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: '16px', lineHeight: 1.3 }}>
                                        {product.title?.[lang] || ''}
                                    </Typography>
                                    {product.itinerary_note?.[lang] && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                            {product.itinerary_note[lang]}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: "flex-end" }} flexWrap="wrap" useFlexGap>
                                {product.time && (
                                    <Chip
                                        icon={<AccessTime sx={{ fontSize: 16 }} />}
                                        label={product.time}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                                {product.pickup_time && (
                                    <Chip
                                        icon={<Schedule sx={{ fontSize: 16 }} />}
                                        label={product.pickup_time}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                                {isFlight && (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent={lang === 'ar' ? "flex-start" : "flex-end"}>
                                        {product.flight_details?.departure_date && (
                                            <Chip
                                                label={`${lang === 'ar' ? 'مغادرة' : 'Dep'}: ${formatDate(product.flight_details.departure_date, lang)}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '11px', fontWeight: 500 }}
                                            />
                                        )}
                                        {product.flight_details?.arrival_date && (
                                            <Chip
                                                label={`${lang === 'ar' ? 'وصول' : 'Arr'}: ${formatDate(product.flight_details.arrival_date, lang)}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '11px', fontWeight: 500 }}
                                            />
                                        )}
                                        <Chip
                                            icon={<Flight sx={{ fontSize: 16 }} />}
                                            label={renderFlightRoute()}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                bgcolor: "rgba(25, 118, 210, 0.85)",
                                                color: "#fff",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                "& .MuiChip-icon": {
                                                    color: "#fff",
                                                },
                                            }}
                                        />
                                        {product.flight_details?.departure_time && (
                                            <Chip
                                                label={`${product.flight_details.departure_time} → ${product.flight_details.arrival_time || ''}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary' }}
                                            />
                                        )}
                                    </Stack>
                                )}
                                {product.city && (
                                    <Chip
                                        icon={<LocationOn color="white" sx={{ color: "#fff", fontSize: 16 }} />}
                                        label={product.city?.name?.[lang] || product.city?.name?.en || product.city?.name?.ar || (typeof product.city === 'string' ? product.city : '')}
                                        size="small"
                                        variant="outlined"
                                        sx={{ bgcolor: "rgba(30, 136, 229, 0.8)", color: "#fff", fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                                {product.units && (
                                    <Chip
                                        label={`${product.units} ${lang === 'ar' ? 'وحدة' : 'Units'}`}
                                        size="small"
                                        color={getColor()}
                                        sx={{ fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                            </Stack>
                        </Stack>

                    </Stack>
                )}
            </Box>
        </Paper>
    );
};

export default ProgramProductItem;
